import { useCallback, useEffect, useRef, useState, type ChangeEvent, type MouseEvent, type UIEvent } from 'react';
import {
  Combobox,
  InputBase,
  Loader,
  ScrollArea,
  useCombobox,
} from '@mantine/core';
import { IconChevronDown, IconX } from '@tabler/icons-react';
import { FormField } from '../FormField';
import { Button } from '../../atoms/Button';
import { Group } from '../../atoms/Group';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';

export interface AsyncSearchOption {
  value: string;
  label: string;
}

export interface AsyncSearchPageResult {
  rows: AsyncSearchOption[];
  total: number;
}

export interface AsyncSearchSelectProps extends CoreProps {
  label?: string;
  placeholder?: string;
  value: string;
  /** Label for the currently selected value (kept when not on the current page). */
  valueLabel?: string;
  onChange: (value: string, label: string) => void;
  /** Fetches one page of options. Called on open, search, and load-more. */
  fetchPage: (args: {
    search: string;
    page: number;
    pageLimit: number;
  }) => Promise<AsyncSearchPageResult>;
  pageLimit?: number;
  debounceMs?: number;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
}

/**
 * Searchable, server-paginated select.
 * Search happens in the selection input itself (not a separate dropdown search bar).
 * Renders dropdown in a portal so it works inside BottomDrawer / Modal.
 */
export function AsyncSearchSelect({
  label,
  placeholder = 'Search…',
  value,
  valueLabel,
  onChange,
  fetchPage,
  pageLimit = 20,
  debounceMs = 300,
  error,
  required,
  disabled,
  clearable = true,
  testId,
  view = 'all',
}: AsyncSearchSelectProps) {
  const shouldRender = useShouldRender(view);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => {
      void loadPage(1, '', false);
    },
  });

  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<AsyncSearchOption[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestId = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const selectedLabel =
    valueLabel ||
    options.find((o) => o.value === value)?.label ||
    (value ? value : '');

  const hasMore = options.length < total;
  const displayValue = combobox.dropdownOpened
    ? search
    : value
      ? selectedLabel
      : '';

  const loadPage = useCallback(
    async (nextPage: number, nextSearch: string, append: boolean) => {
      const id = ++requestId.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setFetchError('');
      try {
        const result = await fetchPageRef.current({
          search: nextSearch,
          page: nextPage,
          pageLimit,
        });
        if (id !== requestId.current) return;
        const rows = Array.isArray(result.rows) ? result.rows : [];
        setTotal(Number(result.total) || 0);
        setPage(nextPage);
        setOptions((prev) => {
          if (!append) return rows;
          const seen = new Set(prev.map((p) => p.value));
          return [...prev, ...rows.filter((r) => !seen.has(r.value))];
        });
      } catch (e) {
        if (id !== requestId.current) return;
        setFetchError(e instanceof Error ? e.message : 'Failed to load options');
        if (!append) setOptions([]);
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [pageLimit],
  );

  function handleSearchChange(q: string) {
    setSearch(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void loadPage(1, q.trim(), false);
    }, debounceMs);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    if (!hasMore || loadingMore || loading) return;
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom) void loadPage(page + 1, search.trim(), true);
  }

  function selectOption(optionValue: string) {
    const opt = options.find((o) => o.value === optionValue);
    onChange(optionValue, opt?.label ?? optionValue);
    setSearch('');
    combobox.closeDropdown();
  }

  function clear() {
    onChange('', '');
    setSearch('');
  }

  if (!shouldRender) return null;

  return (
    <FormField label={label} required={required} error={error || fetchError || undefined}>
      <Combobox
        store={combobox}
        withinPortal
        zIndex={400}
        onOptionSubmit={selectOption}
      >
        <Combobox.Target>
          <InputBase
            data-testid={testId}
            disabled={disabled}
            placeholder={placeholder}
            value={displayValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleSearchChange(e.currentTarget.value);
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            rightSection={
              loading && combobox.dropdownOpened ? (
                <Loader size={14} />
              ) : clearable && value && !combobox.dropdownOpened ? (
                <IconX
                  size={14}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    clear();
                  }}
                />
              ) : (
                <IconChevronDown size={14} />
              )
            }
            rightSectionPointerEvents={
              clearable && value && !combobox.dropdownOpened ? 'all' : 'none'
            }
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize
              mah={240}
              type="scroll"
              viewportProps={{
                onScroll: (e: UIEvent<HTMLDivElement>) => handleScroll(e),
              }}
            >
              {options.length === 0 && !loading ? (
                <Combobox.Empty>No results</Combobox.Empty>
              ) : (
                options.map((item) => (
                  <Combobox.Option
                    value={item.value}
                    key={item.value}
                    active={item.value === value}
                  >
                    {item.label}
                  </Combobox.Option>
                ))
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
          {(hasMore || loadingMore) && (
            <Combobox.Footer>
              <Group justify="center" py={4}>
                <Button
                  size="xs"
                  variant="subtle"
                  loading={loadingMore}
                  onClick={() => void loadPage(page + 1, search.trim(), true)}
                >
                  {loadingMore
                    ? 'Loading…'
                    : `Load more (${options.length}/${total})`}
                </Button>
              </Group>
            </Combobox.Footer>
          )}
        </Combobox.Dropdown>
      </Combobox>
    </FormField>
  );
}
